import { useI18n } from '@i18n';
import { Badge, Box, ButtonProps, Stack } from '@mui/material';
import { portfolio_bool_exp } from '@predium/client-graphql';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import xor from 'lodash/xor';
import { ReactNode, useCallback, useRef, useState } from 'react';
import SearchBoxWithLazyLoading, { SearchItemType } from '@/components/search/SearchBoxWithLazyLoading';
import usePosthogTracking from '@/hooks/usePosthogTracking';
import {
  ALL_PORTFOLIOS_ITEM,
  usePortfolioSelector,
  type UsePortfolioSelectorOptions,
} from '@/sections/StatusQuoAnalysis/NavigationSelectors/Hooks/usePortfolioSelector';
import NavigationSelectorButton from '@/sections/StatusQuoAnalysis/NavigationSelectors/NavigationSelectorButton';

type Props = {
  filtersComponent?: ReactNode;
  /**
   * Lighter Typography style for the portfolio selector
   */
  compact?: boolean;
  variant?: ButtonProps['variant'];
  defaultPortfolioId?: ReturnType<typeof ALL_PORTFOLIOS_ITEM>['value'];
  /** Extra `portfolio` table filters merged with search and id lookups. */
  portfolioWhere?: portfolio_bool_exp | null;
} & (
  | { selectionMode?: 'url' }
  | {
      selectionMode: 'local';
      portfolioIds: number[];
      onPortfolioIdsChange: (portfolioIds: number[]) => void;
    }
);

export const ALL_PORTFOLIOS = 'all_portfolios';
const PORTFOLIO_QUERY_LIMIT = 10;

export default function PortfolioSelector(props: Props) {
  const { filtersComponent, compact = false, variant = 'text', defaultPortfolioId, portfolioWhere } = props;

  const { t } = useI18n();
  const { trackEvent } = usePosthogTracking();

  const portfolioSelectorButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isPortfolioSelectorOpen, setIsPortfolioSelectorOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const hookOptions: UsePortfolioSelectorOptions =
    props.selectionMode === 'local'
      ? {
          debouncedSearchTerm,
          defaultPortfolioId,
          portfolioWhere,
          selectionMode: 'local',
          portfolioIds: props.portfolioIds,
          onPortfolioIdsChange: props.onPortfolioIdsChange,
        }
      : {
          debouncedSearchTerm,
          defaultPortfolioId,
          portfolioWhere,
        };

  const {
    portfolios,
    loadedPortfolios,
    portfolioIds,
    setPortfoliosIds,
    loading,
    loadingByIds,
    hasMore,
    refetch,
    loadMore,
    selectedItems,
    queryParams,
    setQueryParams,
  } = usePortfolioSelector(hookOptions, PORTFOLIO_QUERY_LIMIT);

  const onPortfolioSelected = (selectedPortfolio: SearchItemType) => {
    trackEvent('PORTFOLIO_SELECTED', {
      portfolio_id: selectedPortfolio.value,
    });

    setPortfoliosIds((prev) => {
      if (selectedPortfolio.value === ALL_PORTFOLIOS_ITEM().value) {
        return prev.includes(ALL_PORTFOLIOS_ITEM().value) ? [] : [ALL_PORTFOLIOS_ITEM().value];
      }
      return xor(
        prev.filter((p) => p !== ALL_PORTFOLIOS_ITEM().value),
        [selectedPortfolio.value],
      );
    });
  };

  // "All portfolios" is the absence of a filter, so it must not be counted as one.
  const count = (queryParams?.portfolioIds ?? []).filter((id) => id !== ALL_PORTFOLIOS_ITEM().value).length;
  const portfolio = loadedPortfolios.find((portfolio) => portfolio.value === queryParams?.portfolioIds?.[0]);

  const selectorLabel =
    count > 1
      ? `${count} ${t('General_Portfolio', {
          count: count,
        })}`
      : (portfolio?.label ?? t('General_AllPortfolios'));

  const handleClickOpenSearchBox = useCallback(() => {
    setIsPortfolioSelectorOpen((prev) => !prev);
  }, []);

  const handleClear = () => {
    void setQueryParams({
      portfolioIds: null,
      buildingIds: null,
    });
    setIsPortfolioSelectorOpen(false);

    trackEvent('PORTFOLIO_SELECTED', {
      portfolio_id: ALL_PORTFOLIOS,
    });
  };

  const isLoading = loadingByIds && !loadedPortfolios.length;

  return (
    <Stack direction="row" alignItems={'center'} gap={1}>
      <Box>
        <Badge badgeContent={variant === 'text' ? 0 : count} color="error">
          <NavigationSelectorButton
            ref={portfolioSelectorButtonRef}
            title={selectorLabel}
            onClick={handleClickOpenSearchBox}
            isSelected={isPortfolioSelectorOpen}
            tooltipText={t('General_SelectPortfolio')}
            compact={compact}
            variant={variant}
            isLoading={isLoading}
            buttonSx={variant === 'text' ? undefined : { px: 2, py: '5px' }}
          />
        </Badge>
        <SearchBoxWithLazyLoading
          isOpen={isPortfolioSelectorOpen}
          anchorRef={portfolioSelectorButtonRef}
          refetch={refetch}
          onItemSelected={onPortfolioSelected}
          selectedItems={selectedItems}
          isLoading={loading}
          listItems={portfolios}
          loadMore={loadMore}
          limit={PORTFOLIO_QUERY_LIMIT}
          hasMore={hasMore}
          setHasMore={() => {}} //  Do not want to change the SearchBoxWithLazyLoading API
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          debouncedSearchTerm={debouncedSearchTerm}
          noDataText={t('StatusQuoAnalysisFilterBarPortfolio_NoPortfoliosFound')}
          placeholder={t('General_SearchPortfolioAction')}
          handleClear={handleClear}
          isMultiSelect
          showClearButton={count > 0 || selectedItems.length > 0}
          onHideSearchBox={() => {
            setIsPortfolioSelectorOpen(false);
            const filteredArray = queryParams?.portfolioIds ?? [];
            setPortfoliosIds(filteredArray);
          }}
          handleClick={() => {
            void setQueryParams({
              portfolioIds: portfolioIds.length === 0 ? null : portfolioIds,
            });
            setIsPortfolioSelectorOpen(false);
          }}
          disableApplyButton={
            isEqual(sortBy(portfolioIds), sortBy(queryParams?.portfolioIds ?? [])) || selectedItems.length === 0
          }
        />
      </Box>
      {filtersComponent ? <Box>{filtersComponent}</Box> : null}
    </Stack>
  );
}
